// The overrides of a derived Internal Review figure (SEP-243 section 26).
//
// # The defect this module repairs
//
// The Internal Review held two bare numbers, `totalInvestableAssetsOverride`
// and `totalClientWealthOverride`. Each one replaced a computed figure. Neither
// one carried a reason, a user or a timestamp, so an override erased the
// judgement of the system with no trace, and no reviewer could see that the
// figure in front of them was not the one the platform calculated.
//
// Section 26 asks for three things together: the CALCULATED value stays visible
// beside the override, the override carries a mandatory rationale, and the peer
// reviewer and Compliance both see it. A `SuitabilityCalculationOverride` row
// holds all three, and the row is APPEND-ONLY: a withdrawal adds a status and
// never deletes.
//
// # The key set is open by design
//
// `SUITABILITY_CALCULATION_KEYS` names the figures the form lets an IM
// override. `computeSuitabilityAssessment` reads an OVERRIDES MAP keyed by
// these values, and not two named parameters, so a third overridable figure
// needs no change to the signature of the helper.
//
// # The two bare columns are READ-ONLY for one release
//
// The columns are still on `SuitabilityAssessment`, because a value that an IM
// typed before this change must still answer. THEY TAKE NO NEW VALUE:
// `SaveSuitabilityAssessment` refuses a save that moves either column, and the
// two boxes of the form no longer accept a keystroke. That is the expand half
// of expand and contract, and slice 15 drops the columns.
// `resolveCalculationOverrides` folds the two sources into one map, figure by
// figure: the register retires the column of a figure it carries, and the
// column answers for a figure the register never carried.
// `apps/server/scripts/copy-legacy-suitability-overrides.ts` copies the stored
// column values into the register before the drop.
//
// This module holds no ontology import. It is the wire truth (ADR-0003).

import { z } from 'zod';

/**
 * The derived figures of the Internal Review that an IM may override.
 *
 * Each value is a wire field name of `SuitabilityAssessmentDerived`, so a
 * reader of an override row knows exactly which figure the row replaced.
 *
 * `vendor/septima-ontology-wealth/packages/ontology-wealth/src/suitability-base-figures.ts` holds the same
 * list, because the ontology never depends on the contract. The parity test
 * `apps/server/src/modules/desk/__tests__/suitability-base-figure-parity.test.ts`
 * pins the two lists and the two arithmetic definitions to each other.
 */
export const SUITABILITY_CALCULATION_KEYS = ['totalInvestableAssets', 'totalClientWealth'] as const;

export type SuitabilityCalculationKey = (typeof SUITABILITY_CALCULATION_KEYS)[number];

export const suitabilityCalculationKeySchema = z.enum(SUITABILITY_CALCULATION_KEYS);

/**
 * The labels the screen prints for each overridable figure.
 *
 * Each key here is a camelCase wire field name, so the snake-case guard has
 * nothing to say about it.
 */
export const SUITABILITY_CALCULATION_LABELS: Readonly<Record<SuitabilityCalculationKey, string>> = {
    totalInvestableAssets: 'Total Investable Assets',
    totalClientWealth: 'Total Client Wealth',
};

/** The label of one overridable figure, or the raw key when the set lost it. */
export function suitabilityCalculationLabel(key: string): string {
    return (SUITABILITY_CALCULATION_LABELS as Record<string, string | undefined>)[key] ?? key;
}

/**
 * The override map that `computeSuitabilityAssessment` reads.
 *
 * A key that the map omits, or that carries `null`, leaves the computed figure
 * as it is. A key that carries a number replaces it.
 *
 * The type is a PLAIN OBJECT and it holds no class and no ontology type, so
 * both the browser form and the server read the same shape.
 */
export type SuitabilityCalculationOverrides = {
    readonly [K in SuitabilityCalculationKey]?: number | null;
};

/** An override row is live, or a member of staff withdrew it. */
export const suitabilityOverrideStatusSchema = z.enum(['active', 'withdrawn']);
export type SuitabilityOverrideStatus = z.infer<typeof suitabilityOverrideStatusSchema>;

/**
 * One append-only override of one derived figure.
 *
 * `calculatedValue` is what the platform computed at the moment of the
 * override. It is a HISTORICAL FACT and not a cached derivation: the reviewer
 * must see the figure the override replaced, even after a later edit moves the
 * raw inputs. The server stamps it from its own computation, and it never reads
 * it from a request body.
 */
export const suitabilityCalculationOverrideSchema = z.object({
    id: z.string(),
    assessmentId: z.string(),
    /**
     * The locked review version the override belongs to. Null until slice 11
     * gives the Internal Review its versions.
     */
    versionId: z.string().nullable(),
    clientId: z.string(),
    calculationKey: z.string(),
    /** The label of the figure, for a reader that holds no key table. */
    calculationLabel: z.string(),
    /** What the platform computed. The override never erases it. */
    calculatedValue: z.number(),
    /** What the IM entered in its place. */
    overrideValue: z.number(),
    /** Why. The Action refuses an empty reason. */
    reason: z.string(),
    status: suitabilityOverrideStatusSchema,
    createdByUserId: z.string().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
    withdrawnAt: z.string().nullable(),
    withdrawnByUserId: z.string().nullable(),
    withdrawnByName: z.string().nullable(),
    withdrawalReason: z.string().nullable(),
});
export type SuitabilityCalculationOverrideDto = z.infer<
    typeof suitabilityCalculationOverrideSchema
>;

/** The two bare columns of the review, while they still exist. */
export interface LegacyCalculationOverrides {
    readonly totalInvestableAssetsOverride: number | null;
    readonly totalClientWealthOverride: number | null;
}

/** Which bare column of the review holds the legacy value of each figure. */
const LEGACY_COLUMN_OF: Readonly<
    Record<SuitabilityCalculationKey, keyof LegacyCalculationOverrides>
> = {
    totalInvestableAssets: 'totalInvestableAssetsOverride',
    totalClientWealth: 'totalClientWealthOverride',
};

/**
 * Fold the register and the two bare columns into one override map.
 *
 * THE RULE: THE REGISTER RETIRES THE COLUMN, FIGURE BY FIGURE. One row of any
 * status for a figure takes that figure off the bare column for good:
 *
 *   - the newest ACTIVE row of the figure answers;
 *   - when every row of the figure is withdrawn, the CALCULATED figure of the
 *     platform answers again, which is what `WithdrawSuitabilityCalculationOverride`
 *     tells the reader it does;
 *   - a figure the register never carried falls back to the bare column, which
 *     is what a review that nobody has overridden since the change still holds.
 *
 * The middle rule is the reason the fold reads the withdrawn rows. Without it a
 * withdrawal would hand the figure back to the bare column, so the review would
 * show a third number that appears on no register row and carries no reason.
 * `apps/server/scripts/copy-legacy-suitability-overrides.ts` follows the same
 * rule: it copies a column value only when the register holds NO row of that
 * figure, because a register that already carried the figure retired it.
 *
 * The fold is pure, and nothing stores its result. The form, the controller and
 * the PDF each call it, so the three cannot disagree.
 */
export function resolveCalculationOverrides(input: {
    readonly legacy: LegacyCalculationOverrides;
    readonly entries: ReadonlyArray<
        Pick<
            SuitabilityCalculationOverrideDto,
            'calculationKey' | 'overrideValue' | 'status' | 'createdAt'
        >
    >;
}): SuitabilityCalculationOverrides {
    const out: { -readonly [K in SuitabilityCalculationKey]?: number | null } = {};
    for (const key of SUITABILITY_CALCULATION_KEYS) {
        const held = input.entries.filter((e) => e.calculationKey === key);
        if (held.length === 0) {
            out[key] = input.legacy[LEGACY_COLUMN_OF[key]];
            continue;
        }
        const active = held
            .filter((e) => e.status === 'active')
            .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        out[key] = active[active.length - 1]?.overrideValue ?? null;
    }
    return out;
}

/**
 * What the two cells of section 5 of the printed form hold for one figure.
 *
 * The form prints an AUTOMATIC cell and an OVERRIDE cell side by side, so the
 * reader of the filed advice document sees both numbers. `calculated` is the
 * figure the platform computes from the raw inputs of the review TODAY, and
 * `override` is the effective override, or null when no override stands.
 */
export interface SuitabilityCalculationCell {
    readonly calculated: number;
    readonly override: number | null;
}

export type SuitabilityCalculationCells = Readonly<
    Record<SuitabilityCalculationKey, SuitabilityCalculationCell>
>;

/**
 * Pair the calculated figures with the effective overrides.
 *
 * `calculated` comes from `computeSuitabilityBaseFigures`, which reads the raw
 * inputs alone. The argument is a plain record and not a derived block, so this
 * module never imports the compute module and the two stay free of a cycle.
 */
export function suitabilityCalculationCells(
    calculated: Readonly<Record<SuitabilityCalculationKey, number>>,
    overrides: SuitabilityCalculationOverrides,
): SuitabilityCalculationCells {
    const out: { -readonly [K in SuitabilityCalculationKey]?: SuitabilityCalculationCell } = {};
    for (const key of SUITABILITY_CALCULATION_KEYS) {
        out[key] = { calculated: calculated[key], override: overrides[key] ?? null };
    }
    return out as SuitabilityCalculationCells;
}

/** The active override of one figure, or null. Newest first. */
export function activeOverrideFor(
    entries: ReadonlyArray<SuitabilityCalculationOverrideDto>,
    key: SuitabilityCalculationKey,
): SuitabilityCalculationOverrideDto | null {
    const active = entries
        .filter((e) => e.status === 'active' && e.calculationKey === key)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return active[active.length - 1] ?? null;
}
